import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  tenantId: string;
  employeeId: string;
  designationId?: string | null;
  designationName?: string | null;
  branchId?: string | null;
  /** Multi-branch (Phase 1+): Branch Managers only. `branchId` above stays the primary for older code. */
  primaryBranchId?: string | null;
  branches?: AuthBranch[];
  avatar?: string;
  permissions?: string[];
  /** Read by Profile/Settings MFA toggles; present on the login payload when MFA is configured. */
  mfaEnabled?: boolean;
}

/** One branch a Branch Manager login may work in, as /auth/login and /auth/me return it. */
export interface AuthBranch {
  branchId: string;
  name: string | null;
  code: string | null;
  city: string | null;
  isActive: boolean | null;
  isPrimary: boolean;
  assignmentType: 'permanent' | 'temporary';
  startDate: string | null;
  endDate: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresMfa: boolean;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem('hrms_user') || 'null'),
  accessToken: localStorage.getItem('hrms_access_token'),
  refreshToken: localStorage.getItem('hrms_refresh_token'),
  isAuthenticated: !!localStorage.getItem('hrms_access_token'),
  isLoading: false,
  error: null,
  requiresMfa: false,
};

export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: { identifier: string; password: string; mfaCode?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  },
);

export const logoutAsync = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await api.post('/auth/logout');
  } catch {}
  dispatch(logout());
});

export const getMeAsync = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/auth/me');
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to get user');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('hrms_access_token');
      localStorage.removeItem('hrms_refresh_token');
      localStorage.removeItem('hrms_user');
    },
    refreshTokenAction: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string }>) => {
      state.accessToken = action.payload.accessToken;
      localStorage.setItem('hrms_access_token', action.payload.accessToken);
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem('hrms_refresh_token', action.payload.refreshToken);
      }
    },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.requiresMfa) {
          state.requiresMfa = true;
          return;
        }
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.requiresMfa = false;
        localStorage.setItem('hrms_access_token', action.payload.accessToken);
        localStorage.setItem('hrms_refresh_token', action.payload.refreshToken);
        localStorage.setItem('hrms_user', JSON.stringify(action.payload.user));
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getMeAsync.fulfilled, (state, action) => {
        state.user = action.payload;
        localStorage.setItem('hrms_user', JSON.stringify(action.payload));
      });
  },
});

export const { logout, refreshTokenAction, clearError } = authSlice.actions;
export default authSlice.reducer;
